import React, { useEffect } from 'react';

// ==============================================
// MAPEO DE GRUPOS PROFESIONALES A SCHEMA.ORG
// ==============================================

const GRUPOS_MEDICOS_TRADICIONALES = ['Médicos Cirujanos'];

const GRUPOS_PROFESIONALES_SALUD_ALIADOS = [
  'Enfermeros',
  'Cirujanos Dentistas',
  'Matrones',
  'Enfermera/Matrona',
  'Tecnólogos Médicos',
  'Farmacéuticos y Químico Farmacéuticos',
  'Bioquímicos',
  'Psicólogos',
  'Nutricionistas',
  'Kinesiólogos',
  'Terapeutas Ocupacionales',
  'Fonoaudiólogos'
];

const MAPEO_MEDICAL_SPECIALTY = {
  'Médicos Cirujanos': 'PrimaryCare',
  'Enfermeros': 'Nursing',
  'Cirujanos Dentistas': 'Dentistry',
  'Matrones': 'Midwifery',
  'Enfermera/Matrona': 'Midwifery',
  'Tecnólogos Médicos': 'LaboratoryScience',
  'Farmacéuticos y Químico Farmacéuticos': 'PharmacySpecialty',
  'Bioquímicos': 'LaboratoryScience',
  'Psicólogos': 'Psychology',
  'Nutricionistas': 'DietNutrition',
  'Kinesiólogos': 'Physiotherapy',
  'Terapeutas Ocupacionales': 'Physiotherapy',
  'Fonoaudiólogos': 'SpeechPathology'
};

// ==============================================
// FUNCIONES AUXILIARES
// ==============================================

const determinarEstructura = (profesionalInfo) => {
  const grupoProfesional = profesionalInfo?.perfil?.grupo_profesional;
  
  if (!grupoProfesional) {
    return {
      personType: 'Person',
      businessType: 'HealthAndBeautyBusiness',
      incluirMedicalSpecialty: false
    };
  }

  if (GRUPOS_MEDICOS_TRADICIONALES.includes(grupoProfesional)) {
    // Detectar si el médico atiende SOLO online (sin consultorio ni domicilio)
    const soloOnline = profesionalInfo?.servicio_remoto && 
                       !profesionalInfo?.servicio_consulta && 
                       !profesionalInfo?.servicio_domicilio;
    
    return {
      // Si solo online → 'Person' (no requiere address)
      // Si tiene ubicación física → 'Physician' (requiere address pero la tendrá)
      personType: soloOnline ? 'Person' : 'Physician',
      businessType: 'MedicalOrganization', // Siempre MedicalOrganization para profesionales médicos
      incluirMedicalSpecialty: true // Siempre incluir especialidad médica
    };
  }

  if (GRUPOS_PROFESIONALES_SALUD_ALIADOS.includes(grupoProfesional)) {
    return {
      personType: 'Person',
      businessType: 'MedicalOrganization', // Siempre MedicalOrganization para profesionales de salud
      incluirMedicalSpecialty: true // Siempre incluir especialidad médica
    };
  }

  // Técnicos, auxiliares, otros
  return {
    personType: 'Person',
    businessType: 'HealthAndBeautyBusiness',
    incluirMedicalSpecialty: false
  };
};

const mapearMedicalSpecialty = (grupoProfesional) => {
  return MAPEO_MEDICAL_SPECIALTY[grupoProfesional] || null;
};

const extraerPrecio = (precioString) => {
  if (!precioString || precioString === 'GRATIS' || precioString === 'Gratis') {
    return '0';
  }
  const numerico = precioString.replace(/[^\d]/g, '');
  return numerico || '0';
};


const extraerDuracion = (duracionString) => {
  if (!duracionString) return 60;
  const match = duracionString.match(/\d+/);
  return match ? parseInt(match[0]) : 60;
};

const crearPostalAddress = (cobertura_consulta, consulta_comuna) => {
  // Validar que tengamos datos
  if (!cobertura_consulta || !consulta_comuna) {
    return null;
  }
  
  // Parsear dirección: formato "Calle 123, Comuna"
  const partes = cobertura_consulta.split(',');
  
  if (partes.length < 2) {
    // Si no hay coma, usar todo como streetAddress
    return {
      '@type': 'PostalAddress',
      streetAddress: cobertura_consulta.trim(),
      addressLocality: consulta_comuna,
      addressCountry: 'CL'
    };
  }
  
  return {
    '@type': 'PostalAddress',
    streetAddress: partes[0].trim(),
    addressLocality: consulta_comuna,
    addressCountry: 'CL'
  };
};

const calcularFechaPublicacion = (tiempoRelativo) => {
  if (!tiempoRelativo) {
    return new Date().toISOString().split('T')[0];
  }

  const matchDias = tiempoRelativo.match(/hace (\d+) día/);
  const matchSemanas = tiempoRelativo.match(/hace (\d+) semana/);
  const matchMeses = tiempoRelativo.match(/hace (\d+) mes/);
  const matchAnios = tiempoRelativo.match(/hace (\d+) año/);

  const fecha = new Date();

  if (matchDias) {
    fecha.setDate(fecha.getDate() - parseInt(matchDias[1]));
  } else if (matchSemanas) {
    fecha.setDate(fecha.getDate() - (parseInt(matchSemanas[1]) * 7));
  } else if (matchMeses) {
    fecha.setMonth(fecha.getMonth() - parseInt(matchMeses[1]));
  } else if (matchAnios) {
    fecha.setFullYear(fecha.getFullYear() - parseInt(matchAnios[1]));
  }

  return fecha.toISOString().split('T')[0];
};

const crearAreaServed = (cobertura_domicilio) => {
  // Validar que sea array no vacío
  if (!Array.isArray(cobertura_domicilio) || cobertura_domicilio.length === 0) {
    return null;
  }
  
  // Crear array de objetos City
  return cobertura_domicilio.map(comuna => ({
    '@type': 'City',
    name: comuna
  }));
};



// ==============================================
// FUNCIONES DE GENERACIÓN DE ENTIDADES
// ==============================================

const crearBusinessEntity = (profesionalInfo, estructura) => {
  const { perfil } = profesionalInfo;
  const baseUrl = `https://clinifica.com/pro/${perfil.profesional_link_plataforma}`;

  const business = {
    '@type': estructura.businessType,
    '@id': `${baseUrl}#business`,
    name: `${perfil.profesional_nombre_plataforma} - ${perfil.profesional_profesion}`,
    description: perfil.seo_descripcion_corta || '',
    image: perfil.profesional_foto_perfil || '',
    url: baseUrl,
    employee: {
      '@id': `${baseUrl}#person`
    }
  };

  // Agregar address según modalidad:
  // 1. Consultorio físico → address completa (calle + comuna) - prioridad
  // 2. Solo domicilio (sin consultorio) → address mínima (primera comuna)
  // 3. Solo online (sin consultorio ni domicilio) → sin address (correcto, no requiere ubicación física)
  if (profesionalInfo.servicio_consulta && profesionalInfo.cobertura_consulta) {
    const address = crearPostalAddress(
      profesionalInfo.cobertura_consulta,
      profesionalInfo.consulta_comuna
    );
    if (address) {
      business.address = address;
    }
  }
  // Agregar address mínimo si solo atiende a domicilio (requerido por Google cuando hay areaServed)
  else if (profesionalInfo.servicio_domicilio && profesionalInfo.cobertura_domicilio && profesionalInfo.cobertura_domicilio.length > 0) {
    // Usar la primera comuna como base de operaciones
    business.address = {
      '@type': 'PostalAddress',
      addressLocality: profesionalInfo.cobertura_domicilio[0],
      addressCountry: 'CL'
    };
  }

  // Agregar areaServed si atiende a domicilio
  if (profesionalInfo.servicio_domicilio && profesionalInfo.cobertura_domicilio) {
    const areaServed = crearAreaServed(profesionalInfo.cobertura_domicilio);
    if (areaServed) {
      business.areaServed = areaServed;
    }
  }

  // priceRange ELIMINADO - No se usa más para evitar warnings en MedicalOrganization

  // Agregar medicalSpecialty para profesionales de salud (Grupos 1 y 2 usan MedicalOrganization)
  if (estructura.incluirMedicalSpecialty) {
    const specialty = mapearMedicalSpecialty(perfil.grupo_profesional);
    if (specialty) {
      business.medicalSpecialty = specialty;
    }
  }

  // Agregar aggregateRating si hay evaluaciones
  if (profesionalInfo.evaluaciones && profesionalInfo.evaluaciones.cantidad > 0) {
    business.aggregateRating = {
      '@id': '#rating'
    };
  }

  // Agregar hasOfferCatalog si hay servicios
  if (profesionalInfo.servicios && profesionalInfo.servicios.length > 0) {
    business.hasOfferCatalog = {
      '@type': 'OfferCatalog',
      itemListElement: profesionalInfo.servicios.map((_, index) => ({
        '@id': `#servicio-${index}`
      }))
    };
  }

  return business;
};

const crearPersonEntity = (profesionalInfo, estructura) => {
  const { perfil } = profesionalInfo;
  const baseUrl = `https://clinifica.com/pro/${perfil.profesional_link_plataforma}`;

  return {
    '@type': estructura.personType,
    '@id': `${baseUrl}#person`,
    name: perfil.profesional_nombre_plataforma || '',
    image: perfil.profesional_foto_perfil || '',
    jobTitle: perfil.profesional_profesion || '',
    description: perfil.seo_descripcion_corta || '',
    url: baseUrl
  };
};

const crearOfferEntity = (servicio, index, profesionalInfo, estructura) => {
  const baseUrl = `https://clinifica.com/pro/${profesionalInfo.perfil.profesional_link_plataforma}`;
  
  const precio = extraerPrecio(servicio.servicio_precio);
  const duracion = extraerDuracion(servicio.servicio_duracion);
  
  // Determinar availability
  const tieneDisponibilidad = servicio.disponibilidad && 
    Object.keys(servicio.disponibilidad).length > 0;
  
  const offer = {
    '@type': 'Offer',
    '@id': `#servicio-${index}`,
    name: servicio.servicio_nombre || '',
    description: servicio.servicio_descripcion || '',
    price: precio,
    priceCurrency: 'CLP',
    availability: tieneDisponibilidad ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    seller: {
      '@id': `${baseUrl}#person`
    },
    // ELIMINADO: availableDeliveryMethod - no aplica para servicios de salud
    eligibleDuration: {
      '@type': 'QuantitativeValue',
      value: duracion,
      unitCode: 'MIN'
    }
  };

  // Determinar tipo de procedimiento
  const itemOfferedType = estructura.businessType === 'MedicalOrganization'
    ? 'MedicalProcedure' 
    : 'Service';

  offer.itemOffered = {
    '@type': itemOfferedType,
    name: servicio.servicio_nombre || ''
  };

  // ELIMINADO: procedureType - solo acepta NoninvasiveProcedure/PercutaneousProcedure
  // que no aplican a servicios ambulatorios. Mejor omitir que tener valor inválido.

  return offer;
};

const crearAggregateRating = (evaluaciones) => {
  if (!evaluaciones || evaluaciones.cantidad === 0) {
    return null;
  }

  return {
    '@type': 'AggregateRating',
    '@id': '#rating',
    ratingValue: evaluaciones.promedio || '5.0',
    reviewCount: String(evaluaciones.cantidad || 0),
    bestRating: '5',
    worstRating: '1'
  };
};

const crearReviewEntity = (evaluacion, index, baseUrl) => {
  return {
    '@type': 'Review',
    '@id': `#review-${index}`,
    itemReviewed: {
      '@id': `${baseUrl}#business` // Cambiado de #person a #business
    },
    author: {
      '@type': 'Person',
      name: evaluacion.nombre || 'Paciente Anónimo'
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: String(evaluacion.nota || 5)
    },
    reviewBody: evaluacion.comentario || '',
    datePublished: calcularFechaPublicacion(evaluacion.tiempo_relativo)
  };
};

// ==============================================
// FUNCIÓN PRINCIPAL DE GENERACIÓN
// ==============================================

const generateMedicalBusinessGraph = (profesionalInfo) => {
  if (!profesionalInfo || !profesionalInfo.perfil) {
    return null;
  }

  const estructura = determinarEstructura(profesionalInfo);
  const baseUrl = `https://clinifica.com/pro/${profesionalInfo.perfil.profesional_link_plataforma}`;
  const graph = [];

  // 1. Business Entity (MedicalOrganization o HealthAndBeautyBusiness)
  graph.push(crearBusinessEntity(profesionalInfo, estructura));

  // 2. Person Entity (Person o Physician)
  graph.push(crearPersonEntity(profesionalInfo, estructura));

  // 3. Offers (uno por servicio)
  if (profesionalInfo.servicios && profesionalInfo.servicios.length > 0) {
    profesionalInfo.servicios.forEach((servicio, index) => {
      graph.push(crearOfferEntity(servicio, index, profesionalInfo, estructura));
    });
  }

  // 4. AggregateRating (solo si hay evaluaciones)
  if (profesionalInfo.evaluaciones && profesionalInfo.evaluaciones.cantidad > 0) {
    const rating = crearAggregateRating(profesionalInfo.evaluaciones);
    if (rating) {
      graph.push(rating);
    }
  }

  // 5. Reviews (primeras 3 con comentario)
  if (profesionalInfo.evaluaciones && profesionalInfo.evaluaciones.comentarios) {
    const comentariosConTexto = profesionalInfo.evaluaciones.comentarios.filter(
      c => c.comentario && c.comentario.trim() !== ''
    );
    
    comentariosConTexto.slice(0, 3).forEach((evaluacion, index) => {
      graph.push(crearReviewEntity(evaluacion, index, baseUrl));
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph
  };
};

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================

function SEOProfesionalInjector({ profesionalInfo }) {
  // Generar JSON-LD
  const structuredData = profesionalInfo ? generateMedicalBusinessGraph(profesionalInfo) : null;

  // Efecto para actualizar todos los elementos SEO en el DOM
  useEffect(() => {
    // Guardar valores originales para cleanup
    const originalTitle = document.title;
    let originalDescription = '';
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      originalDescription = descriptionMeta.getAttribute('content') || '';
    }

    // 1. ACTUALIZAR TITLE
    if (profesionalInfo?.perfil?.profesional_nombre_plataforma) {
      // Formato: "Profesión - Nombre Completo" (ej: "Psicólogo - Juan Pérez")
      const profesion = profesionalInfo.perfil.profesional_profesion || '';
      const nombre = profesionalInfo.perfil.profesional_nombre_plataforma;
      document.title = profesion ? `${profesion} - ${nombre}` : nombre;
    } else {
      document.title = 'Clinifica'; // Fallback durante loading
    }

    // 2. ACTUALIZAR META DESCRIPTION
    if (!descriptionMeta) {
      // Si no existe la meta tag, crearla
      descriptionMeta = document.createElement('meta');
      descriptionMeta.setAttribute('name', 'description');
      document.head.appendChild(descriptionMeta);
    }
    
    if (profesionalInfo?.perfil?.seo_descripcion_corta) {
      descriptionMeta.setAttribute('content', profesionalInfo.perfil.seo_descripcion_corta);
    } else {
      descriptionMeta.setAttribute('content', ''); // Fallback durante loading
    }

    // 3. INYECTAR JSON-LD (solo si hay datos)
    let script = null;
    if (structuredData) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData, null, 2);
      script.setAttribute('data-structured-data', 'clinifica-professional');
      document.head.appendChild(script);
    }

    // CLEANUP: Restaurar valores originales y remover script
    return () => {
      document.title = originalTitle;
      if (descriptionMeta && document.head.contains(descriptionMeta)) {
        descriptionMeta.setAttribute('content', originalDescription);
      }
      if (script && document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [profesionalInfo, structuredData]); // Dependencias: profesionalInfo para title/description, structuredData para JSON-LD

  // Este componente no renderiza nada visualmente (solo manipula DOM)
  return null;
}

export default SEOProfesionalInjector;
